import {useRouter} from 'next/router';
import {parseCookies} from 'nookies';
import type React from 'react';
import {type ReactNode, useCallback, useEffect, useMemo, useState} from 'react';
import {HOME} from '../../../config/constants/routes';
import {clearDatadogUser, setDatadogUser} from '../../../lib/datadog';
import {logout} from '../../repositories/logoutRepository';
import {useUsersMe} from '../../repositories/usersMeRepository';
import {
  type AuthAction,
  AuthContext,
  type AuthContextType,
  type AuthState,
  defaultAuthState,
} from './AuthContext';
import {destroyCookieWithLogout} from './destroyCookieWithLogout';
import {isGraduatedUser} from './isGraduatedUser';
import {useTokensInCookies} from './useTokensInCookies';

type Props = {
  children: ReactNode;
  override?: AuthContextType;
};

export const AuthProvider: React.FC<Props> = ({children, override}) => {
  const cookies = parseCookies();
  const tokens = useTokensInCookies(cookies);

  const router = useRouter();

  const [authState, setAuthState] = useState<AuthState>(() => {
    if (!override) return defaultAuthState;
    const {signOut: _s, ...state} = override;
    return state;
  });

  const signOut = useCallback(async () => {
    const cookies = parseCookies();
    await logout({cookies});
    destroyCookieWithLogout();
    clearDatadogUser();

    if (router.pathname === HOME) {
      router.reload(); // トップページでログアウトした場合表示が更新されないためreloadする
      return;
    }

    await router.push(HOME);
  }, [router]);

  const authAction: AuthAction = useMemo(
    () => ({
      signOut: override?.signOut ?? signOut,
    }),
    [override, signOut],
  );

  useEffect(() => {
    if (override) {
      const {signOut: _s, ...state} = override;
      setAuthState(state);
      return;
    }
    if (tokens) {
      setAuthState((prev) => ({
        ...prev,
        status: 'loading',
        accessToken: tokens.accessToken,
        user: undefined,
        isGraduatedUser: false,
        possiblyAuthenticated: true,
      }));
    } else {
      setAuthState((prev) => ({
        ...prev,
        status: 'unauthenticated',
        accessToken: undefined,
        user: undefined,
        isGraduatedUser: false,
        possiblyAuthenticated: false,
      }));
    }
  }, [tokens, override]);

  useUsersMe({
    config: {
      enabled: tokens !== undefined && override === undefined,
      onSuccess(data) {
        if (data.ok) {
          setDatadogUser({id: String(data.value.id)});
          setAuthState((prev) => {
            return {
              ...prev,
              status: 'authenticated',
              // biome-ignore lint/style/noNonNullAssertion: if enabled, tokens exist
              accessToken: tokens!.accessToken,
              user: data.value,
              isGraduatedUser: isGraduatedUser(
                data.value.rawGraduationYear,
                data.value.rawGraduationMonth,
              ),
              possiblyAuthenticated: true,
            };
          });
        }
      },
      onError() {
        setAuthState((prev) => {
          return {
            ...prev,
            status: 'error',
            accessToken: tokens?.accessToken,
            user: undefined,
            isGraduatedUser: false,
          };
        });
      },
    },
  });

  const authContext = useMemo<AuthContextType>(
    () => ({
      ...authState,
      ...authAction,
    }),
    [authState, authAction],
  );

  return (
    <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>
  );
};
